/**
 * Julian Day, ΔT and time scales
 *
 * Every other module takes a Julian Day in Terrestrial Time (JD_TT) or Universal
 * Time (JD_UT) and must say which. Mixing them silently costs ~70 s today and
 * minutes for 19th-century births — enough to move the Moon by an arc-minute.
 *
 * ΔT = TT − UT is interpolated from Espenak & Meeus polynomials.
 *
 * © Manish Jagdish Thatte
 */

/** JD of the J2000.0 epoch, 2000-01-01 12:00 TT. */
export const J2000 = 2451545.0;

/** JD of 1582-10-15, the first Gregorian day. Anything before this is Julian. */
export const GREGORIAN_START_JD = 2299160.5;

const MS_PER_DAY = 86400000;

/**
 * A Date (an instant, which JavaScript always holds in UTC) → JD in UT.
 *
 * UTC and UT1 differ by under 0.9 s by construction of the leap-second system,
 * which is below anything this project can resolve. We do not model DUT1.
 */
export function julianDayUT(date) {
  const ms = date instanceof Date ? date.getTime() : date;
  if (!Number.isFinite(ms)) throw new TypeError('julianDayUT: not a valid Date or epoch-ms');
  return ms / MS_PER_DAY + 2440587.5;
}

/** JD in UT → the Date for that instant. */
export function dateFromJulianDayUT(jdUT) {
  return new Date(Math.round((jdUT - 2440587.5) * MS_PER_DAY));
}

/**
 * Build a JD from calendar fields directly, choosing the Julian or Gregorian
 * calendar by the date itself. Meeus, *Astronomical Algorithms* 2nd ed., ch. 7.
 *
 * ⚠ `month` is 1-based. `day` may be fractional.
 */
export function julianDayFromCalendar(year, month, day, hour = 0, minute = 0, second = 0) {
  let y = year, m = month;
  if (m <= 2) { y -= 1; m += 12; }
  const dayFrac = day + hour / 24 + minute / 1440 + second / 86400;

  // Is this date in the Gregorian calendar? Decide on the calendar date itself,
  // not on a JD we have not computed yet.
  const gregorian =
    year > 1582 ||
    (year === 1582 && (month > 10 || (month === 10 && dayFrac >= 15)));

  let b = 0;
  if (gregorian) {
    const a = Math.floor(y / 100);
    b = 2 - a + Math.floor(a / 4);
  }
  return (
    Math.floor(365.25 * (y + 4716)) +
    Math.floor(30.6001 * (m + 1)) +
    dayFrac + b - 1524.5
  );
}

/**
 * JD → calendar fields, in whichever calendar was in force on that day.
 * Meeus ch. 7. Returns `calendar: 'julian' | 'gregorian'` because the same
 * written date means two different days either side of the reform, and the UI
 * is required to say which one it is showing.
 */
export function calendarFromJD(jd) {
  if (!Number.isFinite(jd)) throw new TypeError('calendarFromJD: jd must be finite');
  const z = Math.floor(jd + 0.5);
  let f = jd + 0.5 - z;

  let a = z;
  const gregorian = jd >= GREGORIAN_START_JD;
  if (gregorian) {
    const alpha = Math.floor((z - 1867216.25) / 36524.25);
    a = z + 1 + alpha - Math.floor(alpha / 4);
  }

  const b = a + 1524;
  const c = Math.floor((b - 122.1) / 365.25);
  const d = Math.floor(365.25 * c);
  const e = Math.floor((b - d) / 30.6001);

  const dayWhole = b - d - Math.floor(30.6001 * e);
  const month = e < 14 ? e - 1 : e - 13;
  const year = month > 2 ? c - 4716 : c - 4715;

  // Split the fractional day into h/m/s, rounding to the nearest millisecond so
  // that a round trip through julianDayFromCalendar is exact rather than
  // 23:59:59.9999997.
  let totalMs = Math.round(f * MS_PER_DAY);
  if (totalMs >= MS_PER_DAY) totalMs -= MS_PER_DAY; // paranoia; f < 1 already
  const hour = Math.floor(totalMs / 3600000);
  const minute = Math.floor((totalMs % 3600000) / 60000);
  const secondMs = totalMs % 60000;

  return {
    year, month, day: dayWhole,
    hour, minute,
    second: Math.floor(secondMs / 1000),
    millisecond: secondMs % 1000,
    calendar: gregorian ? 'gregorian' : 'julian'
  };
}

/** Decimal year, the argument the ΔT polynomials are written in. */
export function decimalYear(jd) {
  const { year, month, day } = calendarFromJD(jd);
  return year + (month - 0.5) / 12 + (day - 1) / 365.25 / 12;
}

/**
 * ΔT = TT − UT, in seconds, from the Espenak & Meeus piecewise polynomials
 * (NASA Eclipse Web Site, "Polynomial Expressions for Delta T"), which cover
 * −1999 to +3000 in the fifteen ranges below.
 *
 * ⚠ The post-2005 branch is an *extrapolation*, and it runs high: it gives
 * ~74 s for 2025 where the observed value is ~69 s. That is a property of the
 * published model, not a bug here, and it is why `deltaT` is a named, swappable
 * function rather than an inlined expression. A 5 s error moves the Moon by
 * about 0.8″ — below our tolerance — so it is recorded rather than corrected.
 */
export function deltaT(year) {
  if (!Number.isFinite(year)) throw new TypeError('deltaT: year must be finite');
  let u, t;

  if (year < -500) {
    u = (year - 1820) / 100;
    return -20 + 32 * u * u;
  }
  if (year < 500) {
    u = year / 100;
    return poly(u, [10583.6, -1014.41, 33.78311, -5.952053, -0.1798452, 0.022174192, 0.0090316521]);
  }
  if (year < 1600) {
    u = (year - 1000) / 100;
    return poly(u, [1574.2, -556.01, 71.23472, 0.319781, -0.8503463, -0.005050998, 0.0083572073]);
  }
  if (year < 1700) {
    t = year - 1600;
    return poly(t, [120, -0.9808, -0.01532, 1 / 7129]);
  }
  if (year < 1800) {
    t = year - 1700;
    return poly(t, [8.83, 0.1603, -0.0059285, 0.00013336, -1 / 1174000]);
  }
  if (year < 1860) {
    t = year - 1800;
    return poly(t, [13.72, -0.332447, 0.0068612, 0.0041116, -0.00037436,
      0.0000121272, -0.0000001699, 0.000000000875]);
  }
  if (year < 1900) {
    t = year - 1860;
    return poly(t, [7.62, 0.5737, -0.251754, 0.01680668, -0.0004473624, 1 / 233174]);
  }
  if (year < 1920) {
    t = year - 1900;
    return poly(t, [-2.79, 1.494119, -0.0598939, 0.0061966, -0.000197]);
  }
  if (year < 1941) {
    t = year - 1920;
    return poly(t, [21.20, 0.84493, -0.076100, 0.0020936]);
  }
  if (year < 1961) {
    t = year - 1950;
    return poly(t, [29.07, 0.407, -1 / 233, 1 / 2547]);
  }
  if (year < 1986) {
    t = year - 1975;
    return poly(t, [45.45, 1.067, -1 / 260, -1 / 718]);
  }
  if (year < 2005) {
    t = year - 2000;
    return poly(t, [63.86, 0.3345, -0.060374, 0.0017275, 0.000651814, 0.00002373599]);
  }
  if (year < 2050) {
    t = year - 2000;
    return poly(t, [62.92, 0.32217, 0.005589]);
  }
  if (year < 2150) {
    u = (year - 1820) / 100;
    return -20 + 32 * u * u - 0.5628 * (2150 - year);
  }
  u = (year - 1820) / 100;
  return -20 + 32 * u * u;
}

function poly(x, c) {
  let r = 0;
  for (let i = c.length - 1; i >= 0; i--) r = r * x + c[i];
  return r;
}

/** JD_UT → JD_TT. ΔT is added, so TT is always ahead of UT in the modern era. */
export function julianDayTT(jdUT) {
  return jdUT + deltaT(decimalYear(jdUT)) / 86400;
}

/**
 * JD_TT → JD_UT.
 *
 * ΔT is defined as a function of UT, so this inverts by one refinement step.
 * ΔT changes by well under a second per day, so one pass converges far below
 * the millisecond.
 */
export function julianDayUTfromTT(jdTT) {
  const guess = jdTT - deltaT(decimalYear(jdTT)) / 86400;
  return jdTT - deltaT(decimalYear(guess)) / 86400;
}

/** Julian centuries from J2000.0. Takes whichever scale the caller needs. */
export function julianCenturiesJ2000(jd) {
  return (jd - J2000) / 36525;
}

/** Julian millennia from J2000.0, for the long-span precession series. */
export function julianMillenniaJ2000(jd) {
  return (jd - J2000) / 365250;
}
